import styled from '@emotion/styled';
import type { FC } from 'react';
import { useControlled } from '../../../lib/customHooks';
import { FormInput, type TextInputProps } from '../../FormComponents';
import { ToolBar } from './ToolBar';

interface Props extends TextInputProps<string> {
  value?: string;
  isEditable?: boolean;
  onEditStateChange?: (isEditable: boolean) => void;
  onSave: (newValue: string) => void;
  onRemove?: () => void;
  onClear?: () => void;
  onCancel?: () => void;
  onChange?: (newValue: string) => void;
  className?: string;
  withCopyButton?: boolean;
  showToolBarAtHover?: boolean;
  askForConfirmationOnRemove?: boolean;
  confirmRemoveText?: string;
  askForConfirmationOnClear?: boolean;
  confirmClearText?: string;
}

export const EditableTextField: FC<Props> = ({
  value,
  isEditable,
  onEditStateChange,
  onSave,
  onRemove,
  onClear,
  onChange,
  className,
  askForConfirmationOnRemove = false,
  confirmRemoveText,
  askForConfirmationOnClear = false,
  confirmClearText,
  withCopyButton = false,
  showToolBarAtHover = false,
  ...props
}) => {
  const [cacheIsEditable, setCacheIsEditable] = useControlled({
    controlledValue: isEditable,
    initialValue: false,
    name: 'EditableTextField',
    onChange: onEditStateChange,
  });
  const [cacheValue, setCacheValue] = useControlled({
    controlledValue: value,
    initialValue: '',
    name: 'EditableTextField',
    onChange,
  });

  const handleSave = () => {
    setCacheIsEditable(false);
    onSave(cacheValue);
  };

  const handleStartEdit = () => {
    setCacheIsEditable(true);
  };

  const handleKeyEvent = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    }
    if (e.key === 'Escape') {
      setCacheIsEditable(false);
    }
  };

  return (
    <Container className={className} showToolBarAtHover={showToolBarAtHover && !cacheIsEditable}>
      <Input onKeyUp={handleKeyEvent}>
        {cacheIsEditable ? (
          <FormInput
            value={cacheValue}
            onChange={(newValue) => setCacheValue(newValue)}
            autoFocus
            {...props}
          />
        ) : (
          <DefaultTextField>{value}</DefaultTextField>
        )}
      </Input>
      <ToolBar
        value={cacheValue}
        isEditable={cacheIsEditable}
        onEditButtonClick={handleStartEdit}
        onSaveButtonClick={handleSave}
        onRemoveButtonClick={onRemove}
        onClearButtonClick={onClear}
        onCancelButtonClick={() => setCacheIsEditable(false)}
        withCopyButton={withCopyButton}
        askForConfirmationOnRemove={askForConfirmationOnRemove}
        confirmRemoveText={confirmRemoveText}
        askForConfirmationOnClear={askForConfirmationOnClear}
        confirmClearText={confirmClearText}
      />
    </Container>
  );
};

const Container = styled.div<{ showToolBarAtHover?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
  justify-content: flex-start;

  .form-group {
    margin-bottom: unset;

    input {
      height: 21px;
      padding: 0 4px;
    }
  }

  .toolbar {
    display: ${(props) => (props.showToolBarAtHover ? 'none' : 'flex')};
  }

  :hover {
    .toolbar {
      display: flex;
    }
  }
`;

const Input = styled.div`
  max-width: 160px;
`;

const DefaultTextField = styled.div`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;
